import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Service,
  calculateTotalDuration,
  calculateTotalPrice,
  calculateEffectiveTotalDuration,
  hasVariableDuration,
  MAX_SERVICES_PER_APPOINTMENT
} from "@/types/appointment";
import { supabase } from "@/integrations/supabase/client";
import type { ResolvedForfait } from "@/lib/forfaitBooking";
import { BOOKING_CONTACT_CLEARED_EVENT, BOOKING_CONTACT_STORAGE_KEY } from "@/utils/bookingContactStorage";
import {
  restoreTunnelState,
  readTunnelSnapshot,
  applyTunnelKeyRemovals,
  DEFAULT_LOCATION_MODE,
  DEFAULT_ANY_CHOICE,
  type TunnelOutcome,
  type TunnelRestoration,
  type BookingHairdresser,
  type RecurringConfig,
} from "@/lib/booking/tunnelRestoration";
import type { AnyLocationChoice, AnyLocationRetained, LocationMode } from "@/pages/client/book/anyLocation/types";

interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  notes?: string;
}

// `BookingHairdresser` et `RecurringConfig` vivent maintenant dans
// `@/lib/booking/tunnelRestoration` (source unique, tâche 2 du lot 8) : ce
// module ne peut pas importer le contexte (il n'importe rien de React), donc
// aucun cycle ne se forme en les réimportant ici. Réexportés pour les
// consommateurs actuels de `@/contexts/BookingContext`.
export type { BookingHairdresser };

interface BookingContextType {
  // Core booking data
  salonId: string | null;
  setSalonId: (id: string | null) => void;
  locationId: string | null;
  setLocationId: (id: string | null) => void;

  // Multi-service support
  services: Service[];
  setServices: (services: Service[]) => void;
  addService: (service: Service) => boolean; // Returns false if max reached
  removeService: (serviceId: string) => void;
  clearServices: () => void;
  toggleService: (service: Service) => boolean; // Toggle add/remove, returns new state
  reorderServices: (activeId: string, overId: string) => void; // Reorder services via drag & drop
  canAddMoreServices: boolean;

  // Computed values for multi-service
  totalDuration: number;
  totalPrice: number | null;
  hasVariablePrice: boolean;

  // Employee-specific pricing support
  resolvedPrices: Map<string, number>; // serviceId -> effectivePrice for selected employee
  effectiveTotalPrice: number | null; // Total using resolved employee prices

  // Employee-specific duration support
  resolvedDurations: Map<string, number>; // serviceId -> effectiveDuration for selected employee
  effectiveTotalDuration: number; // Total using resolved employee durations
  hasVariableDuration: boolean; // True if any service has variable duration

  // Resolution failure surfacing (lot 8, tâche 6) : `supabase.rpc` ne lève
  // pas sur une erreur, elle la rend dans `{ error }`. Lire enfin ce champ
  // rend visible ce qui retombait en silence sur le catalogue.
  resolutionStatus: "idle" | "loading" | "ready" | "failed";
  failedResolution: { priceServiceIds: string[]; durationServiceIds: string[] };
  retryResolution: () => void;

  // Legacy single service support (returns first service or null)
  service: Service | null;
  setService: (service: Service | null) => void;

  // Resolution-aware multi-service support (replaces serviceEmployees).
  // Intent is the user's choice (immutable across resolutions). Resolution is
  // computed inside BookingContext when intent / date / time / candidates change.
  serviceEmployeeIntent: Record<string, BookingHairdresser>;
  setServiceEmployeeIntent: (serviceId: string, hairdresser: BookingHairdresser) => void;
  clearServiceEmployeeIntent: () => void;
  applySameEmployeeIntentToAll: (hairdresser: BookingHairdresser) => void;

  /**
   * True iff the user picked "Oui même" on the SameEmployeeModal for the Any
   * path. Read by validateAnyFeasibility (intersection of the pools), the
   * availability engine, and assign_and_lock_any_slots (one employee for all).
   * Resets to false on addService, removeService, clearServices, modal "Non",
   * and any other path that breaks the "same employee" promise : toggleService,
   * setServices et setService dès que la COMPOSITION du panier change (N1,
   * relecture de la tâche 15b). Un réordonnancement (reorderServices) ou un
   * ajout refusé (panier plein) ne le touche pas.
   *
   * Sauvegardé et restauré AVEC `serviceEmployeeIntent`, dans la même clé de
   * stockage : sans ça, le retour de la connexion Google (rechargement complet)
   * ramenait l'intention « n'importe qui » mais remettait ce drapeau à `false`,
   * et la RPC pouvait donner deux employés à une cliente qui avait demandé la
   * même personne pour tout (revue des tâches 14 et 15, constat I1).
   */
  sameEmployeeForAll: boolean;
  setSameEmployeeForAll: (v: boolean) => void;

  date: Date | null;
  setDate: (date: Date | null) => void;
  time: string | null;
  setTime: (time: string | null) => void;
  customerInfo: CustomerInfo | null;
  setCustomerInfo: (info: CustomerInfo | null) => void;
  contactOwnerUserId: string | null;
  setContactOwnerUserId: (userId: string | null) => void;

  // Booking metadata
  bookingSource: 'direct_link' | null;
  setBookingSource: (source: 'direct_link' | null) => void;
  isDirectBooking: boolean;
  setIsDirectBooking: (isDirect: boolean) => void;
  recurringConfig: RecurringConfig | null;
  setRecurringConfig: (config: RecurringConfig | null) => void;

  // Actions
  saveBookingData: () => void;
  clearBookingData: (preserveForConnectedUser?: boolean) => void;
  ensureTunnelForSalon: (routeSalonId: string | null) => TunnelOutcome | "unchanged";

  // Legacy support
  selectedLocationId: string | null;
  setSelectedLocationId: (id: string | null) => void;

  // Forfait overlay state
  selectedForfait: ResolvedForfait | null;
  setSelectedForfait: (f: ResolvedForfait | null) => void;

  // « N'importe quel emplacement » (lot 4). `specific` = comportement d'aujourd'hui,
  // `selectedLocationId` reste la seule source de l'emplacement précis.
  locationMode: LocationMode;
  setLocationMode: (m: LocationMode) => void;
  anyChoice: AnyLocationChoice;
  setAnyGroupIds: (ids: string[]) => void;
  setAnyRetained: (r: AnyLocationRetained | null) => void;

  /**
   * Frozen forfait price for a service line, or null when none applies.
   * A forfait line's `price_override` is the exact amount the customer is
   * charged (the server trigger rewrites the booked row identically). Every
   * client price display MUST prefer it over the catalog/employee-resolved
   * price — otherwise the recap and confirmation understate (or zero out, for
   * a variable line) the bundle the customer actually agreed to.
   */
  getForfaitOverride: (serviceId: string) => number | null;
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

/**
 * Même COMPOSITION de panier : les mêmes services, dans n'importe quel ordre.
 * Un simple réordonnancement ne défait pas « le même pour tout ».
 */
function sameComposition(a: readonly Service[], b: readonly Service[]): boolean {
  if (a.length !== b.length) return false;
  const ids = new Set(a.map((s) => s.id));
  return b.every((s) => ids.has(s.id));
}

/**
 * Mêmes entrées, dans n'importe quel ordre. Sert à garder la RÉFÉRENCE d'une
 * `Map` de résolution quand une nouvelle résolution rend les mêmes valeurs :
 * le calendrier et la disponibilité multi-employés l'ont en dépendance d'effet,
 * et chaque identité neuve relance leurs appels réseau.
 */
function sameEntries<K, V>(a: Map<K, V>, b: Map<K, V>): boolean {
  return a.size === b.size && [...a].every(([k, v]) => b.has(k) && Object.is(b.get(k), v));
}
function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}
type FailedResolution = { priceServiceIds: string[]; durationServiceIds: string[] };
const sameFailed = (a: FailedResolution, b: FailedResolution) =>
  sameList(a.durationServiceIds, b.durationServiceIds) && sameList(a.priceServiceIds, b.priceServiceIds);

const BOOKING_STORAGE_KEY = 'flowcut_booking_data';
// Même clé que la purge externe (déconnexion / changement de compte) : une
// seule source pour ne pas laisser les deux diverger.
const SENSITIVE_DATA_KEY = BOOKING_CONTACT_STORAGE_KEY; // sessionStorage for sensitive data
const BOOKING_MODE_KEY = 'flowcut_booking_mode'; // Persist booking mode across sessions

export const BookingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // La restauration est calculée UNE fois par instance du fournisseur, pendant
  // le rendu, et non dans un effet : le contexte naît restauré, donc le premier
  // rendu de Book lit déjà le tunnel. Le retrait des clés périmées est
  // idempotent, ce qui le rend sûr sous le double rendu du mode strict.
  const seedRef = useRef<TunnelRestoration | null>(null);
  if (seedRef.current === null) {
    seedRef.current = restoreTunnelState(readTunnelSnapshot());
    applyTunnelKeyRemovals(seedRef.current.keysToRemove);
  }
  const seed = seedRef.current;

  const [salonId, setSalonId] = useState<string | null>(seed.mode?.salonId ?? seed.tunnel?.salonId ?? null);
  const [locationId, setLocationId] = useState<string | null>(seed.tunnel?.locationId ?? null);
  const [services, setServicesState] = useState<Service[]>(seed.tunnel?.services ?? []);
  const [date, setDate] = useState<Date | null>(seed.tunnel?.date ?? null);
  const [time, setTime] = useState<string | null>(seed.tunnel?.time ?? null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(seed.contact?.info ?? null);
  const [contactOwnerUserId, setContactOwnerUserId] = useState<string | null>(seed.contact?.ownerUserId ?? null);
  const [bookingSource, setBookingSource] =
    useState<'direct_link' | null>(seed.mode?.bookingSource ?? null);
  const [isDirectBooking, setIsDirectBooking] = useState(seed.mode?.isDirectBooking ?? false);
  const [recurringConfig, setRecurringConfig] = useState<RecurringConfig | null>(seed.tunnel?.recurringConfig ?? null);
  const [resolvedPrices, setResolvedPrices] = useState<Map<string, number>>(new Map());
  const [resolvedDurations, setResolvedDurations] = useState<Map<string, number>>(new Map());
  const [serviceEmployeeIntent, setServiceEmployeeIntentState] =
    useState<Record<string, BookingHairdresser>>(seed.tunnel?.serviceEmployeeIntent ?? {});
  const [sameEmployeeForAll, setSameEmployeeForAllState] = useState(seed.tunnel?.sameEmployeeForAll ?? false);
  const [selectedForfait, setSelectedForfaitState] = useState<ResolvedForfait | null>(seed.tunnel?.selectedForfait ?? null);
  const [locationMode, setLocationModeState] = useState<LocationMode>(seed.tunnel?.locationMode ?? DEFAULT_LOCATION_MODE);
  const [anyChoice, setAnyChoice] = useState<AnyLocationChoice>(seed.tunnel?.anyChoice ?? DEFAULT_ANY_CHOICE);
  // Idempotent : même `kind` = même référence, donc aucune sauvegarde de plus.
  const setLocationMode = useCallback(
    (m: LocationMode) => setLocationModeState((p) => (p.kind === m.kind ? p : m)),
    [],
  );
  const setAnyGroupIds = useCallback(
    (groupIds: string[]) => setAnyChoice((prev) => ({ ...prev, groupIds })),
    [],
  );
  const setAnyRetained = useCallback(
    (retained: AnyLocationRetained | null) => setAnyChoice((prev) => ({ ...prev, retained })),
    [],
  );

  // Panier COURANT, y compris les écritures pas encore rendues, lu de façon
  // synchrone par les mutateurs ci-dessous pour savoir si un geste change
  // vraiment la composition du panier, et donc s'il défait « le même pour
  // tout » (N1, relecture de la tâche 15b).
  // ⛔ Pas de remise à zéro DANS une fonction de mise à jour de `services` :
  // React peut l'exécuter plus tard, pendant le rendu, et un `false` posé là
  // passerait APRÈS un `setSameEmployeeForAll(true)` du même lot, qu'il
  // annulerait en silence.
  // ⛔ Et TOUTE écriture de `services` passe par `writeServices`, qui met ce
  // ref à jour tout de suite (M1, re-relecture 15c). Un ref recopié seulement
  // au rendu restait périmé dans un lot : après `ensureTunnelForSalon()`, un
  // `addService` du même lot voyait encore le panier vide, prenait les
  // services restaurés pour des ajouts et faisait retomber le drapeau restauré.
  const servicesRef = useRef<Service[]>(services);
  servicesRef.current = services;
  const writeServices = useCallback((next: Service[]) => {
    servicesRef.current = next;
    setServicesState(next);
  }, []);

  const location = useLocation();

  // Per-service forfait price overrides, derived from the selected bundle.
  // serviceId -> price_override. A line without an override (e.g. an "au choix"
  // line) is absent, so callers fall back to the resolved/catalog price.
  const forfaitPriceOverrides = useMemo(() => {
    const map = new Map<string, number>();
    if (!selectedForfait) return map;
    for (const line of selectedForfait.lines) {
      if (line.kind === 'service' && line.serviceId && line.priceOverride != null) {
        map.set(line.serviceId, line.priceOverride);
      }
    }
    return map;
  }, [selectedForfait]);

  const getForfaitOverride = useCallback(
    (serviceId: string): number | null =>
      forfaitPriceOverrides.has(serviceId) ? forfaitPriceOverrides.get(serviceId)! : null,
    [forfaitPriceOverrides]
  );

  // Computed values for multi-service
  const totalDuration = useMemo(() => calculateTotalDuration(services), [services]);
  const totalPrice = useMemo(() => calculateTotalPrice(services), [services]);
  // A variable service that carries a forfait override is NOT unresolved: its
  // frozen price is concrete, so it must not flip the total to "Prix variable".
  const hasVariablePriceValue = useMemo(
    () => services.some(s => s.price_type === 'variable' && getForfaitOverride(s.id) == null),
    [services, getForfaitOverride]
  );
  const hasVariableDurationValue = useMemo(
    () => hasVariableDuration(services),
    [services]
  );
  const canAddMoreServices = services.length < MAX_SERVICES_PER_APPOINTMENT;

  // Effective total duration using resolved employee durations (falls back to base duration)
  const effectiveTotalDuration = useMemo(() => {
    if (services.length === 0) return 0;
    return calculateEffectiveTotalDuration(services, resolvedDurations);
  }, [services, resolvedDurations]);

  // Effective total price using resolved employee prices (falls back to base price)
  const effectiveTotalPrice = useMemo(() => {
    if (hasVariablePriceValue) return null;
    if (services.length === 0) return null;

    return services.reduce((total, service) => {
      // Prefer the frozen forfait override, then the resolved employee price,
      // then the catalog price. Never silently fall to 0 for a covered line.
      const effectivePrice =
        getForfaitOverride(service.id) ?? resolvedPrices.get(service.id) ?? service.price ?? 0;
      return total + effectivePrice;
    }, 0);
  }, [services, resolvedPrices, hasVariablePriceValue, getForfaitOverride]);

  const [resolutionStatus, setResolutionStatus] =
    useState<"idle" | "loading" | "ready" | "failed">("idle");
  const [failedResolution, setFailedResolution] =
    useState<{ priceServiceIds: string[]; durationServiceIds: string[] }>({
      priceServiceIds: [],
      durationServiceIds: [],
    });
  const [resolutionNonce, setResolutionNonce] = useState(0);
  const retryResolution = useCallback(() => setResolutionNonce((n) => n + 1), []);

  // Per-service auto-resolution: whenever `serviceEmployeeIntent` or `services`
  // changes, resolve each service's effective duration and price against
  // its assigned employee. Seule source de résolution depuis le retrait des
  // fonctions `resolveEmployeePrices`/`resolveEmployeeDurations` (mortes,
  // aucun appelant, lot 8 tâche 5) : produit déjà le bon résultat en
  // mono-service comme en multi-service.
  useEffect(() => {
    if (services.length === 0 || Object.keys(serviceEmployeeIntent).length === 0) {
      setResolvedDurations((prev) => (prev.size === 0 ? prev : new Map()));
      setResolvedPrices((prev) => (prev.size === 0 ? prev : new Map()));
      setResolutionStatus("idle");
      setFailedResolution((prev) =>
        prev.priceServiceIds.length === 0 && prev.durationServiceIds.length === 0
          ? prev
          : { priceServiceIds: [], durationServiceIds: [] });
      return;
    }

    let cancelled = false;
    setResolutionStatus("loading");
    void (async () => {
      const newDurations = new Map<string, number>();
      const newPrices = new Map<string, number>();
      const failedPrices: string[] = [];
      const failedDurations: string[] = [];
      try {
        await Promise.all(
          services.map(async (service) => {
            const emp = serviceEmployeeIntent[service.id];
            if (!emp || emp.id === 'any') return;

            // La grille par niveau ne pose jamais `has_employee_durations` /
            // `has_employee_prices` : sans résolution systématique, le client
            // voyait (et réservait) le prix et la durée de base au lieu de
            // ceux du niveau de l'employé choisi.
            // Durée et prix sont indépendants (même `service.id`, même
            // `emp.id`) : ils partent ensemble, un seul aller-retour.
            const durationCall = service.duration_type !== 'variable'
              ? supabase.rpc('get_effective_service_duration', {
                  p_service_id: service.id,
                  p_hairdresser_id: emp.id,
                })
              : null;
            const priceCall = service.price_type !== 'variable'
              ? supabase.rpc('get_effective_service_price', {
                  p_service_id: service.id,
                  p_hairdresser_id: emp.id,
                })
              : null;
            const [durationRes, priceRes] = await Promise.all([durationCall, priceCall]);

            // ⛔ `supabase.rpc` ne LÈVE pas sur une erreur : elle la rend
            // dans `{ error }`. Ne lire que `data` faisait retomber
            // l'écran sur le catalogue, sans un mot (audit du 2026-09-21,
            // dette C).
            if (durationRes) {
              const { data, error } = durationRes;
              if (error || data === null || data === undefined) {
                failedDurations.push(service.id);
              } else {
                newDurations.set(service.id, Number(data));
              }
            }

            if (priceRes) {
              const { data, error } = priceRes;
              if (error || data === null || data === undefined) {
                failedPrices.push(service.id);
              } else {
                newPrices.set(service.id, Number(data));
              }
            }
          })
        );

        if (cancelled) return;
        // Identifiants triés : l'ordre d'arrivée des réponses ne doit pas
        // fabriquer une liste « différente ».
        const nextFailed: FailedResolution = {
          priceServiceIds: [...failedPrices].sort(),
          durationServiceIds: [...failedDurations].sort(),
        };
        setResolvedDurations((prev) => (sameEntries(prev, newDurations) ? prev : newDurations));
        setResolvedPrices((prev) => (sameEntries(prev, newPrices) ? prev : newPrices));
        setFailedResolution((prev) => (sameFailed(prev, nextFailed) ? prev : nextFailed));
        setResolutionStatus(failedPrices.length || failedDurations.length ? "failed" : "ready");
      } catch (error) {
        if (cancelled) return;
        console.error('[BookingContext] Per-service resolution error:', error);
        // Une exception (réseau, etc.) touche potentiellement TOUS les
        // services nommés : sans valeur partielle fiable, on les marque tous
        // en échec plutôt que d'en oublier un.
        const named = services
          .filter((s) => {
            const e = serviceEmployeeIntent[s.id];
            return e && e.id !== 'any';
          })
          .map((s) => s.id);
        setResolvedDurations((prev) => (prev.size === 0 ? prev : new Map()));
        setResolvedPrices((prev) => (prev.size === 0 ? prev : new Map()));
        const nextFailed: FailedResolution = {
          priceServiceIds: [...named].sort(),
          durationServiceIds: [...named].sort(),
        };
        setFailedResolution((prev) => (sameFailed(prev, nextFailed) ? prev : nextFailed));
        setResolutionStatus("failed");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [services, serviceEmployeeIntent, resolutionNonce]);

  // Multi-service management functions
  const setServices = useCallback((newServices: Service[]) => {
    // Enforce max limit
    const limitedServices = newServices.slice(0, MAX_SERVICES_PER_APPOINTMENT);
    if (!sameComposition(servicesRef.current, limitedServices)) {
      setSameEmployeeForAllState(false);
    }
    writeServices(limitedServices);
  }, [writeServices]);

  const addService = useCallback((service: Service): boolean => {
    // Lu sur le panier COURANT (`servicesRef`, écritures du lot comprises),
    // jamais dans une fonction de mise à jour. Un service déjà au panier, ou un
    // panier plein, ne change rien et ne touche pas au drapeau.
    const current = servicesRef.current;
    if (current.length >= MAX_SERVICES_PER_APPOINTMENT || current.some(s => s.id === service.id)) {
      return false;
    }
    setSameEmployeeForAllState(false);
    writeServices([...current, service]);
    return true;
  }, [writeServices]);

  const removeService = useCallback((serviceId: string) => {
    writeServices(servicesRef.current.filter(s => s.id !== serviceId));
    setServiceEmployeeIntentState(prev => {
      if (!(serviceId in prev)) return prev;
      const { [serviceId]: _removed, ...rest } = prev;
      return rest;
    });
    setSameEmployeeForAllState(false);
  }, [writeServices]);

  const clearServices = useCallback(() => {
    writeServices([]);
    setServiceEmployeeIntentState({});
    setSameEmployeeForAllState(false);
    setSelectedForfaitState(null);
  }, [writeServices]);

  const toggleService = useCallback((service: Service): boolean => {
    // LE geste de l'étape Service. Décocher, ou cocher un service quand le
    // panier n'est pas plein, change la composition : « le même pour tout »
    // ne vaut plus, et la cliente garde des employés indépendants ; la modale
    // ne revient que si le premier service (index 0) est rechoisi. Un ajout
    // refusé (panier plein) ne change rien et ne touche pas au drapeau.
    const current = servicesRef.current;
    if (current.some(s => s.id === service.id)) {
      setSameEmployeeForAllState(false);
      // Remove the service and its employee assignment
      setServiceEmployeeIntentState(prevEmp => {
        if (!(service.id in prevEmp)) return prevEmp;
        const { [service.id]: _removed, ...rest } = prevEmp;
        return rest;
      });
      writeServices(current.filter(s => s.id !== service.id));
      return false;
    }
    if (current.length >= MAX_SERVICES_PER_APPOINTMENT) {
      return false;
    }
    setSameEmployeeForAllState(false);
    writeServices([...current, service]);
    return true;
  }, [writeServices]);

  // Intent-aware setters. Intent is the sole source of truth for the user's
  // per-service employee choice; resolution is derived inside BookingContext.
  const setServiceEmployeeIntent = useCallback(
    (serviceId: string, hairdresser: BookingHairdresser) => {
      setServiceEmployeeIntentState(prev => ({ ...prev, [serviceId]: hairdresser }));
    },
    []
  );

  const clearServiceEmployeeIntent = useCallback(() => {
    setServiceEmployeeIntentState({});
  }, []);

  const applySameEmployeeIntentToAll = useCallback(
    (hairdresser: BookingHairdresser) => {
      const next: Record<string, BookingHairdresser> = {};
      services.forEach(s => {
        next[s.id] = hairdresser;
      });
      setServiceEmployeeIntentState(next);
    },
    [services]
  );

  // Reorder services (for drag & drop)
  const reorderServices = useCallback((activeId: string, overId: string) => {
    const prev = servicesRef.current;
    const oldIndex = prev.findIndex(s => s.id === activeId);
    const newIndex = prev.findIndex(s => s.id === overId);

    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

    const newServices = [...prev];
    const [removed] = newServices.splice(oldIndex, 1);
    newServices.splice(newIndex, 0, removed);

    writeServices(newServices);
  }, [writeServices]);

  // Legacy single service support
  const service = services.length > 0 ? services[0] : null;
  const setService = (svc: Service | null) => {
    // Book.tsx la rappelle (sous le nom `setContextService`) avec le service
    // DÉJÀ seul au panier, dans un effet de synchronisation : ce n'est pas un
    // changement de panier, le drapeau ne tombe que si la composition change.
    const current = servicesRef.current;
    const unchanged = svc ? current.length === 1 && current[0].id === svc.id : current.length === 0;
    if (!unchanged) {
      setSameEmployeeForAllState(false);
    }
    if (svc) {
      writeServices([svc]);
    } else {
      writeServices([]);
    }
  };

  // Save booking data - split sensitive and non-sensitive data
  const saveBookingData = () => {
    // Non-sensitive data in localStorage (longer expiry)
    const bookingData = {
      salonId,
      locationId,
      services, // Now storing array of services
      service: services[0] || null, // Legacy: also store first service for backward compatibility
      serviceEmployeeIntent,
      sameEmployeeForAll,
      selectedForfait,
      date,
      time,
      bookingSource,
      isDirectBooking,
      recurringConfig,
      locationMode,
      anyChoice,
      timestamp: Date.now(),
    };
    localStorage.setItem(BOOKING_STORAGE_KEY, JSON.stringify(bookingData));
    
    // Sensitive data in sessionStorage (shorter expiry, cleared on browser close)
    if (customerInfo) {
      const sensitiveData = {
        customerInfo: {
          name: customerInfo.name || '',
          email: customerInfo.email || '',
          phone: customerInfo.phone || '',
          notes: customerInfo.notes || '',
        },
        ownerUserId: contactOwnerUserId,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(SENSITIVE_DATA_KEY, JSON.stringify(sensitiveData));
    }
  };

  // Clear booking data with conditional preservation for direct bookings
  const clearBookingData = (preserveForConnectedUser = false) => {
    // Preserve booking mode ONLY for direct bookings when user is connected
    if (preserveForConnectedUser && isDirectBooking && salonId) {
      const bookingMode = {
        isDirectBooking: true,
        salonId,
        bookingSource,
        timestamp: Date.now(),
      };
      localStorage.setItem(BOOKING_MODE_KEY, JSON.stringify(bookingMode));
    } else {
      // Clear booking mode for disconnected users
      localStorage.removeItem(BOOKING_MODE_KEY);
    }
    
    // Clear all booking state
    setLocationId(null);
    clearServices(); // Clear all services
    setDate(null);
    setTime(null);
    setCustomerInfo(null);
    setRecurringConfig(null);
    setLocationModeState(DEFAULT_LOCATION_MODE);
    setAnyChoice(DEFAULT_ANY_CHOICE);

    // Clear storage
    localStorage.removeItem(BOOKING_STORAGE_KEY);
    sessionStorage.removeItem(SENSITIVE_DATA_KEY);
    
    // Clear booking mode flags if not preserved
    if (!preserveForConnectedUser || !isDirectBooking || !salonId) {
      setSalonId(null);
      setBookingSource(null);
      setIsDirectBooking(false);
    }
  };

  /**
   * Appelée au montage de `Book`. Ne relit le stockage QUE si le salon de la
   * route diffère du salon en mémoire : la mémoire vivante gagne sur le
   * stockage, que l'auto-sauvegarde garde à jour à une écriture près.
   * C'est le seul geste que faisait réellement l'ancien M1 (cas C9).
   */
  const ensureTunnelForSalon = useCallback(
    (routeSalonId: string | null): TunnelOutcome | "unchanged" => {
      if (!routeSalonId || routeSalonId === salonId) return "unchanged";
      const next = restoreTunnelState(readTunnelSnapshot({ routeSalonId }));
      applyTunnelKeyRemovals(next.keysToRemove);
      setSalonId(next.mode?.salonId ?? next.tunnel?.salonId ?? routeSalonId);
      setLocationId(next.tunnel?.locationId ?? null);
      writeServices(next.tunnel?.services ?? []);
      setServiceEmployeeIntentState(next.tunnel?.serviceEmployeeIntent ?? {});
      setSameEmployeeForAllState(next.tunnel?.sameEmployeeForAll ?? false);
      setSelectedForfaitState(next.tunnel?.selectedForfait ?? null);
      setDate(next.tunnel?.date ?? null);
      setTime(next.tunnel?.time ?? null);
      setRecurringConfig(next.tunnel?.recurringConfig ?? null);
      setLocationModeState(next.tunnel?.locationMode ?? DEFAULT_LOCATION_MODE);
      setAnyChoice(next.tunnel?.anyChoice ?? DEFAULT_ANY_CHOICE);
      setCustomerInfo(next.contact?.info ?? null);
      setContactOwnerUserId(next.contact?.ownerUserId ?? null);
      setIsDirectBooking(next.mode?.isDirectBooking ?? false);
      setBookingSource(next.mode?.bookingSource ?? null);
      return next.outcome;
    },
    [salonId, writeServices],
  );

  // Auto-save booking data when state changes.
  // ⛔ `sameEmployeeForAll` est dans les dépendances : « Non » à la modale ne
  // change QUE ce drapeau, et sans lui l'ancien `true` resterait en stockage.
  // `selectedForfait` y est pour la même raison : le forfait se pose APRÈS le
  // panier, par un geste qui ne touche que lui.
  useEffect(() => {
    if (
      salonId ||
      services.length > 0 ||
      Object.keys(serviceEmployeeIntent).length > 0 ||
      date ||
      time
    ) {
      saveBookingData();
    }
  }, [salonId, locationId, services, serviceEmployeeIntent, sameEmployeeForAll, selectedForfait, date, time, customerInfo, contactOwnerUserId, bookingSource, isDirectBooking, recurringConfig, locationMode, anyChoice]);

  // Purge demandée de l'extérieur (déconnexion, changement de compte).
  //
  // Le stockage est déjà vidé par `clearStoredBookingContact` quand cet
  // événement arrive ; ce qu'il reste à faire est de lâcher la copie EN
  // MÉMOIRE. Sans ça, l'effet d'auto-sauvegarde ci-dessus réécrit les
  // coordonnées du client précédent au tout prochain changement d'état. Le cas
  // vit sur mobile, où la déconnexion est une navigation SPA et non un
  // rechargement complet de la page comme sur le web.
  useEffect(() => {
    const drop = () => setCustomerInfo(null);
    window.addEventListener(BOOKING_CONTACT_CLEARED_EVENT, drop);
    return () => window.removeEventListener(BOOKING_CONTACT_CLEARED_EVENT, drop);
  }, []);

  // Clear booking context when navigating away from booking routes
  useEffect(() => {
    if (!location.pathname.startsWith("/client") && !location.pathname.startsWith("/book")) {
      // Don't clear when going to landing - preserve booking mode for potential return
      // Only clear on non-booking, non-landing routes (e.g., owner/admin auth)
      if (location.pathname.startsWith("/auth/owner") || location.pathname.startsWith("/auth/admin")) {
        clearBookingData(false);
      }
    }
  }, [location.pathname]);

  return (
    <BookingContext.Provider
      value={{
        salonId,
        setSalonId,
        locationId,
        setLocationId,

        // Multi-service support
        services,
        setServices,
        addService,
        removeService,
        clearServices,
        toggleService,
        reorderServices,
        canAddMoreServices,

        // Computed values
        totalDuration,
        totalPrice,
        hasVariablePrice: hasVariablePriceValue,

        // Employee-specific pricing
        resolvedPrices,
        effectiveTotalPrice,

        // Employee-specific durations
        resolvedDurations,
        effectiveTotalDuration,
        hasVariableDuration: hasVariableDurationValue,

        // Resolution failure surfacing
        resolutionStatus,
        failedResolution,
        retryResolution,

        // Legacy single service support
        service,
        setService,

        // Resolution-aware intent + derived resolution
        serviceEmployeeIntent,
        setServiceEmployeeIntent,
        clearServiceEmployeeIntent,
        applySameEmployeeIntentToAll,
        sameEmployeeForAll,
        setSameEmployeeForAll: setSameEmployeeForAllState,

        date,
        setDate,
        time,
        setTime,
        customerInfo,
        setCustomerInfo,
        contactOwnerUserId,
        setContactOwnerUserId,
        bookingSource,
        setBookingSource,
        isDirectBooking,
        setIsDirectBooking,
        recurringConfig,
        setRecurringConfig,
        saveBookingData,
        clearBookingData,
        ensureTunnelForSalon,
        // Legacy support
        selectedLocationId: locationId,
        setSelectedLocationId: setLocationId,

        // Forfait overlay state
        selectedForfait,
        setSelectedForfait: setSelectedForfaitState,
        getForfaitOverride,

        // Mode « n'importe quel emplacement »
        locationMode,
        setLocationMode,
        anyChoice,
        setAnyGroupIds,
        setAnyRetained,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error("useBooking must be used within a BookingProvider");
  }
  return context;
};
